(() => {
  const desktopQuery = window.matchMedia('(min-width: 990px)');
  const mobileQuery = window.matchMedia('(max-width: 989px)');
  const narrowMobileQuery = window.matchMedia('(max-width: 749px)');
  const gallerySelector = 'main[data-template="product"] .product media-gallery';
  const interactiveSelector = 'input, textarea, select, option, [contenteditable="true"], video, audio';
  let enhancementScheduled = false;

  const getMediaItems = (gallery) =>
    Array.from(
      gallery.querySelectorAll(
        '[id^="Slider-Gallery-"] > .product__media-item[data-media-id]'
      )
    );

  const getThumbnailItems = (gallery) =>
    Array.from(
      gallery.querySelectorAll(
        '[id^="Slider-Thumbnails-"] > .thumbnail-list__item[data-target]'
      )
    ).filter((item) => item.querySelector('button:not([disabled])'));

  const getViewer = (gallery) => gallery.querySelector('[id^="GalleryViewer-"]');

  const getActiveMedia = (gallery) =>
    gallery.querySelector(
      '[id^="Slider-Gallery-"] > .product__media-item[data-media-id].is-active'
    ) || getMediaItems(gallery)[0] || null;

  const getNearestMedia = (gallery) => {
    const viewer = getViewer(gallery);
    const slider = viewer?.slider || viewer?.querySelector('[id^="Slider-Gallery-"]');
    const items = getMediaItems(gallery);
    if (!slider || !items.length) return items[0] || null;

    const position = slider.scrollLeft;
    return items.reduce((nearest, item) => {
      if (!nearest) return item;
      return Math.abs(item.offsetLeft - position) < Math.abs(nearest.offsetLeft - position)
        ? item
        : nearest;
    }, null);
  };

  const getCurrentMobileMedia = (gallery) => {
    const nearest = getNearestMedia(gallery);
    if (nearest) return nearest;

    const items = getMediaItems(gallery);
    const currentMediaId = gallery.dataset.gwMobileCurrentMediaId;
    if (currentMediaId) {
      const storedItem = items.find((item) => item.dataset.mediaId === currentMediaId);
      if (storedItem) return storedItem;
    }

    const viewer = getViewer(gallery);
    const currentPage = Number(viewer?.currentPage);
    const sliderItems = viewer?.sliderItemsToShow;
    if (sliderItems?.length && Number.isFinite(currentPage) && currentPage > 0) {
      return sliderItems[currentPage - 1] || null;
    }

    return getActiveMedia(gallery);
  };

  const getActiveIndex = (gallery, items) => {
    const ariaIndex = items.findIndex((item) => item.querySelector('button[aria-current="true"]'));
    if (ariaIndex >= 0) return ariaIndex;

    const activeMediaId = getActiveMedia(gallery)?.dataset.mediaId;
    const mediaIndex = items.findIndex((item) => item.dataset.target === activeMediaId);
    return mediaIndex >= 0 ? mediaIndex : 0;
  };

  const primeVideoElement = (video) => {
    if (!video) return;
    video.defaultMuted = true;
    video.muted = true;
    video.autoplay = true;
    video.playsInline = true;
    video.preload = 'auto';
    video.setAttribute('muted', '');
    video.setAttribute('autoplay', '');
    video.setAttribute('playsinline', '');
    video.setAttribute('webkit-playsinline', '');
    video.setAttribute('preload', 'auto');
  };

  const pauseMediaItem = (item, reset = false) => {
    item?.querySelectorAll('video').forEach((video) => {
      video.pause();
      if (!reset) return;
      try {
        video.currentTime = 0;
      } catch (_) {
        // Seeking can fail before metadata is available.
      }
    });
  };

  const pauseOtherMedia = (gallery, activeItem) => {
    getMediaItems(gallery).forEach((item) => {
      if (item !== activeItem) pauseMediaItem(item, true);
    });
  };

  const removeDirectVideos = (deferredMedia, except = null) => {
    Array.from(deferredMedia?.children || []).forEach((child) => {
      if (child.tagName === 'VIDEO' && child !== except) child.remove();
    });
  };

  const getOrCreateNativeVideo = (deferredMedia) => {
    if (!deferredMedia) return null;

    let video = Array.from(deferredMedia.children).find(
      (child) => child.tagName === 'VIDEO' && child.dataset.gwMobileNativeVideo === 'true'
    );
    if (video) {
      primeVideoElement(video);
      return video;
    }

    const templateVideo = deferredMedia.querySelector('template')?.content?.querySelector('video');
    const loadedVideo = Array.from(deferredMedia.children).find((child) => child.tagName === 'VIDEO');
    const sourceVideo = templateVideo || loadedVideo;
    if (!sourceVideo) return null;

    video = sourceVideo.cloneNode(true);
    video.dataset.gwMobileNativeVideo = 'true';
    primeVideoElement(video);
    removeDirectVideos(deferredMedia);
    deferredMedia.setAttribute('loaded', '');
    deferredMedia.appendChild(video);

    try {
      video.load();
    } catch (_) {
      // The source can already be loading.
    }

    return video;
  };

  const prepareNativeVideos = (gallery) => {
    getMediaItems(gallery).forEach((item) => {
      const deferredMedia = item.querySelector('deferred-media.deferred-media');
      if (!deferredMedia) return;

      const templateVideo = deferredMedia.querySelector('template')?.content?.querySelector('video');
      const directVideo = Array.from(deferredMedia.children).find((child) => child.tagName === 'VIDEO');
      if (!templateVideo && !directVideo) return;

      const video = getOrCreateNativeVideo(deferredMedia);
      if (!video) return;
      video.pause();
      try {
        video.currentTime = 0;
      } catch (_) {
        // Metadata may not be ready yet.
      }
    });
  };

  const syncNarrowMobileActiveState = (gallery, item = null) => {
    if (!narrowMobileQuery.matches) return item;

    const currentItem = item?.matches?.('.product__media-item[data-media-id]')
      ? item
      : getNearestMedia(gallery);
    if (!currentItem) return null;

    getMediaItems(gallery).forEach((mediaItem) => {
      mediaItem.classList.toggle('is-active', mediaItem === currentItem);
    });
    return currentItem;
  };

  const playMobileVideo = (gallery, activeItem) => {
    if (!mobileQuery.matches || !activeItem) return;

    const deferredMedia = activeItem.querySelector('deferred-media.deferred-media');
    const video = getOrCreateNativeVideo(deferredMedia);
    if (!video) return;

    const isStillCurrent = () =>
      mobileQuery.matches &&
      gallery.dataset.gwMobileCurrentMediaId === activeItem.dataset.mediaId &&
      gallery.dataset.gwMobileGalleryVisible === 'true' &&
      document.visibilityState !== 'hidden';

    if (!isStillCurrent()) return;
    primeVideoElement(video);

    const attemptPlay = () => {
      if (!isStillCurrent()) return;
      primeVideoElement(video);
      const promise = video.play();
      if (promise?.catch) promise.catch(() => {});
    };

    if (video.dataset.gwIosAutoplayListeners !== 'true') {
      video.addEventListener('loadedmetadata', attemptPlay);
      video.addEventListener('loadeddata', attemptPlay);
      video.addEventListener('canplay', attemptPlay);
      video.dataset.gwIosAutoplayListeners = 'true';
    }

    attemptPlay();

    let attempts = 0;
    const timer = window.setInterval(() => {
      attempts += 1;
      if (!isStillCurrent() || !video.paused || attempts >= 30) {
        window.clearInterval(timer);
        return;
      }
      attemptPlay();
    }, 150);
  };

  const syncMobileMedia = (gallery, item = null) => {
    if (!mobileQuery.matches) return;

    let currentItem = item?.matches?.('.product__media-item[data-media-id]')
      ? item
      : getCurrentMobileMedia(gallery);
    currentItem = syncNarrowMobileActiveState(gallery, currentItem) || currentItem;
    if (!currentItem) return;

    gallery.dataset.gwMobileCurrentMediaId = currentItem.dataset.mediaId || '';
    pauseOtherMedia(gallery, currentItem);

    if (gallery.dataset.gwMobileGalleryVisible === 'true') playMobileVideo(gallery, currentItem);
    else pauseMediaItem(currentItem);
  };

  const prepareAndPlayDesktopVideo = (gallery, activeItem) => {
    if (!desktopQuery.matches || !activeItem || !activeItem.classList.contains('is-active')) return;

    pauseOtherMedia(gallery, activeItem);

    const deferredMedia = activeItem.querySelector('deferred-media.deferred-media');
    if (deferredMedia && typeof deferredMedia.loadContent === 'function') {
      deferredMedia.loadContent(false);
    }

    const attemptPlay = () => {
      const video = activeItem.querySelector('video');
      if (!video || !activeItem.classList.contains('is-active') || !desktopQuery.matches) return false;
      primeVideoElement(video);
      const promise = video.play();
      if (promise?.catch) promise.catch(() => {});
      return true;
    };

    if (attemptPlay()) return;

    let attempts = 0;
    const timer = window.setInterval(() => {
      attempts += 1;
      if (
        attemptPlay() ||
        attempts >= 20 ||
        !activeItem.classList.contains('is-active') ||
        !desktopQuery.matches
      ) {
        window.clearInterval(timer);
      }
    }, 100);
  };

  const syncActiveThumbnail = (gallery) => {
    const activeMediaId = getActiveMedia(gallery)?.dataset.mediaId;
    getThumbnailItems(gallery).forEach((item) => {
      const button = item.querySelector('button');
      if (!button) return;
      if (item.dataset.target === activeMediaId) button.setAttribute('aria-current', 'true');
      else button.removeAttribute('aria-current');
    });
  };

  const activateMedia = (gallery, mediaId) => {
    if (typeof gallery.setActiveMedia === 'function') {
      gallery.setActiveMedia(mediaId, false);
    } else {
      getMediaItems(gallery).forEach((item) => {
        item.classList.toggle('is-active', item.dataset.mediaId === mediaId);
      });
    }

    window.requestAnimationFrame(() => {
      syncActiveThumbnail(gallery);
      const activeItem = getActiveMedia(gallery);
      if (desktopQuery.matches && activeItem && gallery.dataset.gwDesktopGalleryVisible === 'true') {
        prepareAndPlayDesktopVideo(gallery, activeItem);
      }
    });
  };

  const moveGallery = (gallery, direction) => {
    const items = getThumbnailItems(gallery);
    if (items.length < 2) return;

    const currentIndex = getActiveIndex(gallery, items);
    const targetItem = items[(currentIndex + direction + items.length) % items.length];
    activateMedia(gallery, targetItem.dataset.target);
    targetItem.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
  };

  const observeGalleryVisibility = (gallery) => {
    if (gallery.dataset.gwGalleryVisibilityObserved === 'true') return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const desktopVisible = entry.isIntersecting && entry.intersectionRatio >= 0.35;
          const mobileVisible = entry.isIntersecting && entry.intersectionRatio >= 0.2;
          gallery.dataset.gwDesktopGalleryVisible = desktopVisible ? 'true' : 'false';
          gallery.dataset.gwMobileGalleryVisible = mobileVisible ? 'true' : 'false';

          if (desktopQuery.matches) {
            const activeItem = getActiveMedia(gallery);
            if (desktopVisible) prepareAndPlayDesktopVideo(gallery, activeItem);
            else pauseMediaItem(activeItem, true);
            return;
          }

          if (mobileQuery.matches) {
            const currentItem = getCurrentMobileMedia(gallery);
            if (!currentItem) return;
            if (mobileVisible) syncMobileMedia(gallery, currentItem);
            else pauseMediaItem(currentItem);
          }
        });
      },
      { threshold: [0, 0.2, 0.35, 0.5, 0.75] }
    );

    observer.observe(gallery);
    gallery.dataset.gwGalleryVisibilityObserved = 'true';
  };

  const enhanceGallery = (gallery) => {
    const product = gallery.closest('.product');
    const viewer = getViewer(gallery);
    const mediaItems = getMediaItems(gallery);
    if (!product || !viewer || !mediaItems.length) return;

    if (mediaItems.length > 1) product.classList.add('product--thumbnail_slider');

    if (gallery.dataset.gwGalleryEnhanced !== 'true') {
      gallery.querySelector('.gw-gallery-nav--prev')?.addEventListener('click', () => moveGallery(gallery, -1));
      gallery.querySelector('.gw-gallery-nav--next')?.addEventListener('click', () => moveGallery(gallery, 1));

      viewer.addEventListener('slideChanged', (event) => {
        const currentItem = event.detail?.currentElement;
        if (mobileQuery.matches) {
          syncMobileMedia(gallery, currentItem);
        } else {
          syncActiveThumbnail(gallery);
          const activeItem = getActiveMedia(gallery);
          if (gallery.dataset.gwDesktopGalleryVisible === 'true') {
            prepareAndPlayDesktopVideo(gallery, activeItem);
          }
        }
      });

      const slider = viewer.slider || viewer.querySelector('[id^="Slider-Gallery-"]');
      let scrollTimer;
      slider?.addEventListener(
        'scroll',
        () => {
          if (!mobileQuery.matches) return;
          window.clearTimeout(scrollTimer);
          scrollTimer = window.setTimeout(() => syncMobileMedia(gallery), 80);
        },
        { passive: true }
      );

      const syncAfterGesture = () => {
        if (!mobileQuery.matches) return;
        syncMobileMedia(gallery);
        window.requestAnimationFrame(() => syncMobileMedia(gallery));
      };
      viewer.addEventListener('touchend', syncAfterGesture, { passive: true });
      viewer.addEventListener('pointerup', syncAfterGesture, { passive: true });

      gallery.dataset.gwGalleryEnhanced = 'true';
    }

    if (mobileQuery.matches) {
      prepareNativeVideos(gallery);
      const initialItem = getCurrentMobileMedia(gallery);
      if (initialItem) {
        gallery.dataset.gwMobileCurrentMediaId = initialItem.dataset.mediaId || '';
        syncNarrowMobileActiveState(gallery, initialItem);
      }
    }

    syncActiveThumbnail(gallery);
    observeGalleryVisibility(gallery);
  };

  const enhanceAllGalleries = (root = document) => {
    root.querySelectorAll?.(gallerySelector).forEach(enhanceGallery);
  };

  const syncVisibleGalleries = () => {
    document.querySelectorAll(gallerySelector).forEach((gallery) => {
      if (desktopQuery.matches && gallery.dataset.gwDesktopGalleryVisible === 'true') {
        prepareAndPlayDesktopVideo(gallery, getActiveMedia(gallery));
      } else if (mobileQuery.matches && gallery.dataset.gwMobileGalleryVisible === 'true') {
        syncMobileMedia(gallery);
      }
    });
  };

  const scheduleEnhancement = () => {
    if (enhancementScheduled) return;
    enhancementScheduled = true;
    window.requestAnimationFrame(() => {
      enhancementScheduled = false;
      enhanceAllGalleries();
    });
  };

  const getVisibleGallery = () => {
    const candidates = Array.from(
      document.querySelectorAll(`${gallerySelector}[data-gw-gallery-enhanced="true"]`)
    ).map((gallery) => {
      const rect = gallery.getBoundingClientRect();
      const visibleWidth = Math.max(0, Math.min(rect.right, window.innerWidth) - Math.max(rect.left, 0));
      const visibleHeight = Math.max(0, Math.min(rect.bottom, window.innerHeight) - Math.max(rect.top, 0));
      return { gallery, visibleArea: visibleWidth * visibleHeight };
    });

    candidates.sort((a, b) => b.visibleArea - a.visibleArea);
    return candidates[0]?.visibleArea > 0 ? candidates[0].gallery : null;
  };

  document.addEventListener('keydown', (event) => {
    if (!desktopQuery.matches || (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight')) return;
    if (event.ctrlKey || event.metaKey || event.altKey || event.shiftKey) return;
    if (event.target instanceof Element && event.target.closest(interactiveSelector)) return;
    if (document.querySelector('dialog[open], product-modal[open], .product-media-modal[open]')) return;

    const gallery = getVisibleGallery();
    if (!gallery) return;
    event.preventDefault();
    moveGallery(gallery, event.key === 'ArrowLeft' ? -1 : 1);
  });

  desktopQuery.addEventListener('change', (event) => {
    document.querySelectorAll(gallerySelector).forEach((gallery) => {
      if (event.matches) {
        pauseOtherMedia(gallery, getActiveMedia(gallery));
        if (gallery.dataset.gwDesktopGalleryVisible === 'true') {
          prepareAndPlayDesktopVideo(gallery, getActiveMedia(gallery));
        }
      } else {
        const currentItem = getCurrentMobileMedia(gallery);
        prepareNativeVideos(gallery);
        if (currentItem) {
          gallery.dataset.gwMobileCurrentMediaId = currentItem.dataset.mediaId || '';
          syncNarrowMobileActiveState(gallery, currentItem);
          if (gallery.dataset.gwMobileGalleryVisible === 'true') syncMobileMedia(gallery, currentItem);
        }
      }
    });
  });

  narrowMobileQuery.addEventListener('change', (event) => {
    if (!event.matches) return;
    document.querySelectorAll(gallerySelector).forEach((gallery) => syncMobileMedia(gallery));
  });

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') syncVisibleGalleries();
  });
  window.addEventListener('pageshow', syncVisibleGalleries);
  window.addEventListener('focus', syncVisibleGalleries);

  enhanceAllGalleries();
  document.addEventListener('shopify:section:load', (event) => enhanceAllGalleries(event.target));
  new MutationObserver(scheduleEnhancement).observe(document.documentElement, {
    childList: true,
    subtree: true,
  });
})();
